// src/AppContext.js
import React, { createContext, useState, useEffect } from 'react';
import {
  getDatabase,
  ref,
  onValue,
  off,
  get,
} from 'firebase/database';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { firestore } from './firebase';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';

export const AppContext = createContext();

export const AppProvider = ({ children }) => {
  /* ---------- global data ---------- */
  const [assetsSummary, setAssetsSummary] = useState([]);
  const [blocks, setBlocks]               = useState([]);
  const [owned, setOwned]                 = useState(0);
  const [datWallet, setDatWallet]         = useState({});

  /* ---------- auth-dependent data ---------- */
  const [currentUser, setCurrentUser]     = useState(null);
  const [userId2, setUserId2]             = useState(null);
  const [userWallet, setUserWallet]       = useState(null);
  const [userAssets, setUserAssets]       = useState([]);
  const [myDATS, setMyDATS]               = useState([]);

  /* ---------- credits ---------- */
  const [creditsPurchased, setCreditsPurchased] = useState(0);
  const [creditsEarned, setCreditsEarned]       = useState(0);
  const [creditsRaw, setCreditsRaw]             = useState(0);

  const [isAllowed, setIsAllowed] = useState(false);

  /* ---------- DatChainToken entitlement + live spot value ---------- */
  const [globalUtilityCents, setGlobalUtilityCents] = useState(0);
  const [datTierPriceCents, setDatTierPriceCents]   = useState(25000);

  const [userUtilityCents, setUserUtilityCents]     = useState(0);
  const [userAwardedTokens, setUserAwardedTokens]   = useState(0);

  const [datClaimableTokens, setDatClaimableTokens] = useState(0);
  const [datClaimableUsd, setDatClaimableUsd]       = useState(0);

  // NEW: live pool & circulating supply -> spot price
  const [poolCentsLive, setPoolCentsLive]           = useState(0);
const [circulatingLive, setCirculatingLive] = useState(100000);

  const [datTokenSpotUsd, setDatTokenSpotUsd]       = useState(0);

  // NEW: live DatChainToken wallet (rewards token) qty
  const [datRewardWalletQty, setDatRewardWalletQty] = useState(0);

  const TIER_SCHEDULE = [
    { upToCents: 25_000_000_00, priceCents: 25_000 }, // $0–$25M  -> $250
    { upToCents: 50_000_000_00, priceCents: 30_000 }, // $25–$50M -> $300
    { upToCents: 75_000_000_00, priceCents: 35_000 }, // $50–$75M -> $350
    { upToCents:100_000_000_00, priceCents: 40_000 }, // $75–$100M-> $400
  ];
  const getTierPriceCents = (totalSoldCents) => {
    for (const t of TIER_SCHEDULE) {
      if (totalSoldCents < t.upToCents) return t.priceCents;
    }
    return TIER_SCHEDULE[TIER_SCHEDULE.length - 1].priceCents;
  };

  useEffect(() => {
    setCreditsRaw(creditsPurchased + creditsEarned);
  }, [creditsPurchased, creditsEarned]);

  useEffect(() => {
    const db        = getDatabase();
    const blocksRef = ref(db, 'tokenBlocks');

    (async () => {
      try {
        const summaryDocRef  = doc(firestore, 'assetsSummary', 'main');
        const summaryDocSnap = await getDoc(summaryDocRef);
        if (summaryDocSnap.exists()) {
          const summaryData = summaryDocSnap.data();
          const assets = (summaryData.assets || []).slice().sort(
            (a, b) =>
              (b.releaseTimestamp?.toMillis?.() ?? b.releaseTimestamp ?? 0) -
              (a.releaseTimestamp?.toMillis?.() ?? a.releaseTimestamp ?? 0)
          );
          setAssetsSummary(assets);
        } else {
          setAssetsSummary([]);
        }
      } catch (error) {
        console.error('[assetsSummary] fetch error:', error);
        setAssetsSummary([]);
      }
    })();

    const blocksUnsub = onValue(blocksRef, snap => {
      if (!snap.exists()) return;
      const arr = Object.entries(snap.val()).map(([id, v]) => ({
        id,
        price:             Number(v.price),
        platformRemaining: Number(v.platformRemaining),
        sellQty:           Number(v.sellQty),
        walletId:          v.walletId
      })).sort((a, b) => Number(a.id) - Number(b.id));
      setBlocks(arr);
    });

    const auth = getAuth();

    let rtdbWalletRef=null, rtdbWalletUnsub=null;
    let rtdbCurCreditsRef=null, rtdbCurCreditsUnsub=null;
    let rtdbEarnCreditsRef=null, rtdbEarnCreditsUnsub=null;
    let fsUserUnsub=null, fsWalletUnsub=null;
    let globalUtilRef=null, globalUtilUnsub=null;
    let utilSpendRef=null, utilSpendUnsub=null;

    // NEW: live metrics listeners
    let poolRef=null, poolUnsub=null;
    let circRef=null, circUnsub=null;

    // NEW: DatChainToken wallet listener
    let datRewardRef=null, datRewardUnsub=null;

    const stop = (refObj, unsub) => {
      if (refObj && unsub) { off(refObj, 'value', unsub); }
    };

    const numFromAny = (n) => {
      if (n == null) return 0;
      if (typeof n === 'number') return Number.isFinite(n) ? n : 0;
      if (typeof n === 'string') return Number(n) || 0;
      if (typeof n === 'object') return numFromAny(Object.values(n)[0]);
      return 0;
    };

    const safeTotal = (snap) => {
      const data = snap.val() || {};
      const str  = data.totalCredits ?? '0';
      const num  = Number(String(str).replace(/[^0-9.-]/g, ''));
      return Number.isFinite(num) ? num : 0;
    };

    const authUnsub = onAuthStateChanged(auth, user => {
      stop(rtdbWalletRef, rtdbWalletUnsub);
      stop(rtdbCurCreditsRef, rtdbCurCreditsUnsub);
      stop(rtdbEarnCreditsRef, rtdbEarnCreditsUnsub);
      if (fsUserUnsub)   { fsUserUnsub();   fsUserUnsub=null; }
      if (fsWalletUnsub) { fsWalletUnsub(); fsWalletUnsub=null; }
      stop(globalUtilRef, globalUtilUnsub);
      stop(utilSpendRef, utilSpendUnsub);
      stop(poolRef, poolUnsub);
      stop(circRef, circUnsub);
if (datRewardUnsub) { datRewardUnsub(); datRewardUnsub = null; }
datRewardRef = null;


      setCurrentUser(null);
      setUserWallet(null);
      setUserId2(user?.uid ?? null);

      setCreditsPurchased(0);
      setCreditsEarned(0); 
      setCreditsRaw(0);
 
      setGlobalUtilityCents(0);
      setDatTierPriceCents(25000);
      setUserUtilityCents(0);
      setUserAwardedTokens(0);
      setDatClaimableTokens(0);
      setDatClaimableUsd(0);

      setPoolCentsLive(0);
      setCirculatingLive(100000);
      setDatTokenSpotUsd(0);

      setDatRewardWalletQty(0);

      if (!user) {
        setOwned(0);
        setDatWallet({});
        setUserAssets([]);
        setMyDATS([]);
        return;
      }

      /* RTDB: DAT Chain token wallet (per-block DATs) */
      const db = getDatabase();
      rtdbWalletRef = ref(db, `datchainwallets/${user.uid}`);
      rtdbWalletUnsub = onValue(
        rtdbWalletRef,
        snap => {
          if (!snap.exists()) {
            setOwned(0);
            setDatWallet({});
            return;
          }
          const raw = snap.val();
          const cleaned = Object.fromEntries(
            Object.entries(raw).map(([bid, v]) => {
              const price = numFromAny(v?.price);
              const qty   = numFromAny(v?.qty);
              return [bid, { price, qty }];
            })
          );
          setDatWallet(cleaned);
          const total = Object.values(cleaned).reduce((s, e) => s + (e.qty || 0), 0);
          setOwned(total);
        },
        err => console.error('💥 RTDB wallet listener error', err)
      );

      /* RTDB: credits strings */
      rtdbCurCreditsRef = ref(db, `users/${user.uid}/summary/currentCreditsStrings`);
      rtdbEarnCreditsRef = ref(db, `users/${user.uid}/summary/earnedCreditsStrings`);

      rtdbCurCreditsUnsub = onValue(
        rtdbCurCreditsRef,
        (curSnap) => setCreditsPurchased(safeTotal(curSnap)),
        (error) => console.error('[CurrentCreditsStrings] Error:', error)
      );
      rtdbEarnCreditsUnsub = onValue(
        rtdbEarnCreditsRef,
        (earnSnap) => setCreditsEarned(safeTotal(earnSnap)),
        (error) => console.error('[EarnedCreditsStrings] Error:', error)
      );

      /* FS: users/{uid} */
      const userDocRef = doc(firestore, 'users', user.uid);
      fsUserUnsub = onSnapshot(
        userDocRef,
        (docSnap) => setCurrentUser(docSnap.exists() ? ({ uid: user.uid, ...docSnap.data() }) : ({ uid: user.uid })),
        (error) => console.error('💥 users/{uid} snapshot error:', error)
      );

      /* FS: wallet/main */
      const fsWalletDocRef = doc(firestore, 'users', user.uid, 'wallet', 'main');
      fsWalletUnsub = onSnapshot(
        fsWalletDocRef,
        (walletSnap) => setUserWallet(walletSnap.exists() ? walletSnap.data() : { balance: 0, ownedDats: [] }),
        (error) => console.error('💥 FS wallet snapshot error:', error)
      );

      /* NEW: Global utility sold -> tier */
      globalUtilRef = ref(db, 'metrics/utilitySold/totalCents');
      globalUtilUnsub = onValue(
        globalUtilRef,
        (snap) => {
          const cents = Number(snap.val()) || 0;
          setGlobalUtilityCents(cents);
          setDatTierPriceCents(getTierPriceCents(cents));
        },
        (error) => console.error('💥 global utility listener error:', error)
      );

      /* NEW: User cumulative utility spend */
      utilSpendRef = ref(db, `utilitySpend/${user.uid}`);
      utilSpendUnsub = onValue(
        utilSpendRef,
        (snap) => {
          const v = snap.val() || {};
          setUserUtilityCents(Number(v.totalCents) || 0);
          setUserAwardedTokens(Number(v.awarded) || 0);
        },
        (error) => console.error('💥 user utilitySpend listener error:', error)
      );

      /* NEW: Live pool & circulating supply (maintained by backend) */
      poolRef = ref(db, 'metrics/datToken/poolCents');
      circRef = ref(db, 'metrics/datToken/circulating');

poolUnsub = onValue(
  poolRef,
  (snap) => {
    const v = Number(snap.val());
    setPoolCentsLive(Number.isFinite(v) && v >= 0 ? v : 0);
  },
  (error) => console.error('💥 pool listener error:', error)
);

      circUnsub = onValue(
        circRef,
        (snap) => {
          const v = Number(snap.val());
    setCirculatingLive(Number.isFinite(v) && v > 0 ? v : 100000);
        },
        (error) => console.error('💥 circulating listener error:', error)
      );

      /* NEW: DatChainToken rewards wallet listener */
      datRewardRef = ref(db, `datChainTokenWallets/${user.uid}`);
      datRewardUnsub = onValue(
        datRewardRef,
        (snap) => {
          const v = snap.val() || {};
          const qty = Number(v.qty) || 0;
          setDatRewardWalletQty(qty);
        },
        (error) => console.error('💥 datChainTokenWallet listener error:', error)
      );
    });

    return () => {
      off(blocksRef, 'value', blocksUnsub);
      authUnsub();
    };
  }, []);

useEffect(() => {
  const circ = Number(circulatingLive) || 0;

  // Fallback: if poolCentsLive is 0/empty, use 30% of utility sold
  const fallbackPool = Math.floor((Number(globalUtilityCents) || 0) * 0.30);
  const effectivePoolCents = Number(poolCentsLive) > 0 ? Number(poolCentsLive) : fallbackPool;

  if (circ <= 0 || effectivePoolCents <= 0) {
    setDatTokenSpotUsd(0);
    return;
  }
  setDatTokenSpotUsd(effectivePoolCents / circ / 100); // cents -> dollars
}, [poolCentsLive, circulatingLive, globalUtilityCents]);


  /* Recompute claimable tokens and their live $ value whenever inputs change */
  useEffect(() => {
    const price = Number(datTierPriceCents) || 0;
    if (price <= 0) {
      setDatClaimableTokens(0);
      setDatClaimableUsd(0);
      return;
    }
    const entitled = Math.floor((Number(userUtilityCents) || 0) / price);
    const claimable = Math.max(0, entitled - (Number(userAwardedTokens) || 0));
    setDatClaimableTokens(claimable);
    setDatClaimableUsd(claimable * (Number(datTokenSpotUsd) || 0));
  }, [datTierPriceCents, userUtilityCents, userAwardedTokens, datTokenSpotUsd]);

  /* Derive user assets & myDATS */
  useEffect(() => {
    if (userWallet && assetsSummary) {
      const userOwnedEditions = (userWallet.ownedDats || []).filter(
        (ownedDat) =>
          typeof ownedDat.edition === 'number' &&
          ownedDat.edition >= 1 &&
          ownedDat.edition <= 20
      );

      const nextUserAssets =
        assetsSummary?.map((asset) => {
          const ownedEditions = userOwnedEditions
            .filter((ownedDat) => ownedDat.id === asset.id)
            .map((ownedDat) => {
              const editionDetails = asset.editions.find(
                (edition) => edition.edition === ownedDat.edition
              );
              return { ...editionDetails, id: asset.id, ...asset };
            });

          return ownedEditions.length ? { ...asset, ownedEditions } : null;
        }).filter(Boolean) || [];

      setUserAssets(nextUserAssets);

      const mine = assetsSummary?.filter(a => a.creatorId === userId2) || [];
      setMyDATS(mine);
    }
  }, [userWallet, assetsSummary, userId2]);

    // Keep isAllowed in sync whenever auth state changes
  useEffect(() => {
    const auth = getAuth();

    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setIsAllowed(false);
        return;
      }

      try {
        const allowedRef = doc(firestore, 'portalAllowedUsers', 'allowedUsers');
        const allowedSnap = await getDoc(allowedRef);
        const allowedUsers = allowedSnap.exists() ? allowedSnap.data().users || [] : [];

        setIsAllowed(allowedUsers.includes(user.uid));
      } catch (err) {
        console.error('💥 Error checking allowed users:', err);
        setIsAllowed(false);
      }
    });

    return () => unsub();
  }, []);


  return (
    <AppContext.Provider
      value={{
        // global
        assetsSummary,
        setAssetsSummary,
        blocks,
        owned,
        datWallet,

        // auth-dependent
        currentUser,
        userId2,
        userWallet,

        // derived
        userAssets,
        setUserAssets,
        myDATS,

        // credits
        creditsRaw,
        creditsEarned,
        creditsPurchased,

        isAllowed,
        setIsAllowed,

        // entitlement + live spot
        datClaimableTokens,
        datClaimableUsd,
        datTokenSpotUsd,

        // NEW: expose live DatChainToken wallet qty
        datRewardWalletQty,
      }}
    >
      {children}
    </AppContext.Provider>
  );
};
